import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  Users,
  Globe,
  Lock,
  ChevronRight 
} from "lucide-react";

const MyActivities = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchActivities = async () => {
      if (!user) return;

      try {
        // Consulta simplificada sin orderBy
        const q = query(
          collection(db, "activities"),
          where("creatorUID", "==", user.uid)
        );

        const querySnapshot = await getDocs(q);
        let activitiesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Ordenar los resultados manualmente
        activitiesData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA;
        });

        setActivities(activitiesData);
      } catch (err) {
        console.error("Error fetching activities:", err);
        setError("Could not load activities. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [user]);

  const formatDateTime = (dateTime) => {
    try {
      const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime?.toDate?.() || new Date(dateTime);
      return {
        date: date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        }),
        time: date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    } catch (error) {
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  const ActivityCard = ({ activity }) => {
    const { date, time } = formatDateTime(activity.eventDateTime);

    return (
      <button
        onClick={() => navigate(`/go/${activity.id}`)}
        className="w-full bg-white p-4 rounded-xl shadow-sm space-y-3 hover:bg-gray-50 transition-colors"
      >
        {/* Title and Privacy */}
        <div className="flex justify-between items-start">
          <h3 className="font-medium text-left">{activity.title}</h3>
          {activity.isPublic ? (
            <Globe className="w-4 h-4 text-green-500 flex-shrink-0" />
          ) : (
            <Lock className="w-4 h-4 text-blue-500 flex-shrink-0" />
          )}
        </div>

        {/* Date and Time */}
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>{date}</span>
          <Clock className="w-4 h-4 ml-2" />
          <span>{time}</span>
        </div>

        {/* Location */}
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <MapPin className="w-4 h-4" />
          <span>{activity.location}</span>
        </div>

        {/* Attendees */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Users className="w-4 h-4" />
            <span>{activity.attendees?.length || 0} / {activity.maxAttendees} attendees</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between py-2 px-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-medium">My Activities</h1>
          <div className="w-8" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-600">
            {error}
          </div>
        )}

        {!error && activities.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">You haven't created any activities yet</p>
            <button
              onClick={() => navigate('/newactivity')}
              className="text-orange-500 font-medium hover:text-orange-600"
            >
              Create your first activity
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}
          </div>
        )}
      </div>

      {/* Add Activity Button */}
      {activities.length > 0 && (
        <div className="fixed bottom-16 left-0 right-0 p-4">
          <button
            onClick={() => navigate('/newactivity')}
            className="w-full bg-orange-500 text-white py-3 rounded-xl font-medium hover:bg-orange-600 transition-colors"
          >
            Create New Activity
          </button>
        </div>
      )}
    </div>
  );
};

export default MyActivities;